import React, {
    createContext,
    useContext,
    useEffect,
    useState,
} from 'react';
import { clearStorage, isSSOlogin, getEmailFromToken } from "./authToken";
import jwtAxios, { setAuthToken } from "./auth";
import Swal from "sweetalert2";
import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { usePrevious } from '../lib/usePreviousValue';
import axiosPublic from "./axiosPublic";
import { jwtDecode } from 'jwt-decode';

const JWTAuthContext = createContext();
const JWTAuthActionsContext = createContext();

export const useJWTAuth = () => useContext(JWTAuthContext);
export const useJWTAuthActions = () => useContext(JWTAuthActionsContext);

const JWTAuthProvider = ({
    children,
}) => {
    const { accounts, instance, inProgress } = useMsal();
    const isAuthenticated = useIsAuthenticated();
    const prevValueIsAuthenticated = usePrevious(isAuthenticated);

    const isTokenExpired = (token) => {
        if (!token) return true; // No token means expired
        const { exp } = jwtDecode(token);
        var tokenValid = Date.now() >= exp * 1000;
        return tokenValid; // exp is in seconds
    };

    const setUserDetails = async () => {
        let user;
        if (isSSOlogin()) {
            if (accounts && accounts[0]) {
                try {
                    const res = await axiosPublic.get('/auth/GetUserInfo?userId=' + accounts[0].username);
                    const data = res.data

                    if (data && data.result && !data.result.isActive) {
                        await Swal.fire({
                            position: "center",
                            text: "The user account is deactivated. Please contact your Admin",
                            confirmButtonText: "Ok",
                            icon: "info",
                        }).then(async (result) => {
                            if (result.isConfirmed) {
                                try {
                                    await logout();
                                } catch (error) {
                                    console.error('Error fetching data:', error);
                                }
                            }
                        });
                        return false;
                    }
                    if (data && data.result && data.result.email) {
                        user = data.result;
                        user.resetPasswordStatus = true
                    }
                    else {
                        user = {
                            id: 0,
                            name: accounts[0].name,
                            email: accounts[0].username,
                            isActive: true,
                            resetPasswordStatus: true,
                            uniqueIdentifier: accounts[0].localAccountId,
                            role: 3,
                            roleName: 'BusinessUnituser',
                        }
                    }
                } catch (error) {
                    if (error.response.status === 401) {
                        try {
                            await instance.acquireTokenSilent({
                                account: accounts[0],
                            });
                            window.location.href = '/';
                        } catch (error) {
                            clearStorage();
                            window.location.href = '/login';
                        }
                    }
                }
            }
            else
                await retrySsoSignIn();
        }
        else {
            try {
                const { data } = await jwtAxios.get('/auth/Get');
                user = data.result.userTableDetails;
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        }
        if (!user) {
            clearStorage();
            Swal.fire({
                position: "center",
                text: "Couldn't find the user information. Please try again.",
                confirmButtonText: "Ok",
                icon: "info",
            }).then(async (result) => {
                if (result.isConfirmed) {
                    try {
                        await logout();
                    } catch (error) {
                        console.error('Error fetching data:', error);
                    }
                }
            });
            return false;
        }
        if (!user.resetPasswordStatus) {
            clearStorage();
            localStorage.setItem('userIdentifier', user.uniqueIdentifier)
            window.location.href = '/resetpassword';
        } else {
            try {
                const { data } = await jwtAxios.get("/folders?userId=" + user.uniqueIdentifier);

                // Assign the folder list to the user object
                //user.folders = data.result.folderLists;
                user.folders = data.result.folderLists;
                user.isGlobalAdmin = data.result.isGlobalAdmin ?? user.isGlobalAdmin;

                user.folders.forEach(folder => {
                    // Check if subFolders exists and is an array
                    if (folder.subFolders && Array.isArray(folder.subFolders)) {
                        folder.subFolders.sort((a, b) => a.name.localeCompare(b.name));
                    }
                });
            } catch (error) {
                console.error('Error fetching data:', error);
            }
            setJWTAuthData({
                isLoading: false,
                isAuthenticated: true,
                user: user,
                inProgress
            });
        }
    };

    const refreshFolders = async (callback) => {
        let user = userDetailsData.user;
        try {
            const { data } = await jwtAxios.get("/folders?userId=" + user.uniqueIdentifier);

            // Assign the folder list to the user object
            user.folders = data.result.folderLists;

            // Sort the subFolders alphabetically by 'name' for each folder, if they exist
            user.folders.forEach(folder => {
                // Check if subFolders exists and is an array
                if (folder.subFolders && Array.isArray(folder.subFolders)) {
                    folder.subFolders.sort((a, b) => a.name.localeCompare(b.name));
                }
                // If no subFolders array, it will just skip sorting, avoiding any errors
            });
        } catch (error) {
            console.error('Error fetching data:', error);
        }
        setJWTAuthData({
            isLoading: false,
            isAuthenticated: true,
            user: user,
            inProgress
        });
        if (callback)
            callback(user)
    }

    const [userDetailsData, setJWTAuthData] = useState({
        user: undefined,
        isAuthenticated: false,
        isLoading: true,
        inProgress
    });
    const getAuthUser = async () => {
        const token = localStorage.getItem("tokenDetails");
        let tokenDetails = token ? JSON.parse(token) : undefined;

        if (isSSOlogin() && accounts && accounts[0]) {
            tokenDetails = { accessToken: accounts[0].idToken };
        }

        if (tokenDetails && isTokenExpired(tokenDetails?.accessToken)) {
            Swal.fire({
                position: "center",
                icon: "error",
                text: "Session expired. Try to login again.",
                showConfirmButton: true,
                timer: 3000,
                timerProgressbar: true
            }).then(async (result) => {
                try {
                    //instance.setActiveAccount(accounts[0]);
                    await instance.acquireTokenSilent({
                        account: accounts[0],
                    });
                } catch (error) {
                    console.error('Error fetching data:', error);
                }
            });
        }

        if (!tokenDetails) {
            setJWTAuthData({
                user: null,
                isLoading: false,
                isAuthenticated: false,
                inProgress
            });
            return;
        }

        axiosPublic.defaults.headers.common.Authorization = `Bearer ${tokenDetails.accessToken}`;
        setAuthToken(tokenDetails);
        setUserDetails();
    };


    useEffect(() => {
    if (inProgress === "none") {
        getAuthUser();
    }
}, [isAuthenticated, prevValueIsAuthenticated]);

    const signInUser = async ({
        email,
        password,
    }) => {

       console.log("Axios BaseURL =", jwtAxios.defaults.baseURL);

        try {
            const {
                data
            } = await jwtAxios.post('/auth/Login', {
                userId: email,
                password
            });
            if (!data.result.userTableDetails.resetPasswordStatus) {
                clearStorage();
                localStorage.setItem('userIdentifier', data.result.userTableDetails.uniqueIdentifier)
                window.location.href = '/resetpassword';
            }
            else if (!data.result.userTableDetails.isActive) {
                Swal.fire({
                    position: "center",
                    icon: "info",
                    text: "The user account is deactivated. Please contact your Admin",
                    showConfirmButton: true,
                });
            }
            else {
                setAuthToken(data.result.token);
                setJWTAuthData({
                    user: data.result.userTableDetails,
                    isAuthenticated: true,
                    isLoading: false,
                    inProgress
                });
                window.location.href = '/';
            }
        } catch (error) {
            setJWTAuthData({
                ...userDetailsData,
                isAuthenticated: false,
                isLoading: false,
                inProgress
            });
        }
    };

    const retrySsoSignIn = async () => {
        const email = getEmailFromToken()
        if (email) {
            await ssoSignInUser({ email });
        }
        else {
            clearStorage();
            window.location.href = '/';
        }
    }

    const ssoSignInUser = async ({
        email
    }) => {
        try {
            const request = {
                redirectStartPage: "/",
                scopes: ["openid", "profile", "user.Read"],
                loginHint: email
            };
            localStorage.setItem("ssologin", true);
            await instance.loginRedirect(request)
        } catch (error) {
            setJWTAuthData({
                ...userDetailsData,
                isAuthenticated: false,
                isLoading: false,
                inProgress
            });
        }
    };

    const resetUserPassword = async ({
        userid,
        oldpassword,
        newpassword
    }) => {

        try {
            const {
                data
            } = await jwtAxios.post('/auth/resetpassword', {
                userid,
                oldpassword,
                newpassword
            });
            if (data.statusCode === 200) {
                localStorage.removeItem('userIdentifier');
                Swal.fire({
                    position: "center",
                    icon: "success",
                    text: "Password Reset done Successfully",
                    showConfirmButton: true,
                    timer: 2000,
                    timerProgressbar: true
                }).then(async (result) => {
                    if (result.isConfirmed) {
                        try {
                            setTimeout(() => {
                                window.location.href = '/login';
                            }, 3000);
                        } catch (error) {
                            console.error('Error fetching data:', error);
                        }
                    }
                })
                setTimeout(() => {
                    window.location.href = '/login';
                }, 3000);

            }
        } catch (error) {
            setJWTAuthData({
                ...userDetailsData,
                isAuthenticated: false,
                isLoading: false,
                inProgress
            });
        }
    };

    const changeUserPassword = async ({
        oldpassword,
        newpassword
    }) => {

        try {
            const {
                data
            } = await jwtAxios.post('/auth/changepassword', {
                oldpassword,
                newpassword
            });
            if (data.statusCode === 200) {
                Swal.fire({
                    position: "center",
                    icon: "success",
                    text: "Password changed Successfully, Please login again.",
                    showConfirmButton: true,
                    timer: 4000,
                    timerProgressbar: true
                }).then(async (result) => {
                    await logout();
                })
            }
        } catch (error) {
            console.error('Error fetching data:', error);
        }
    };

    const logout = async () => {
        clearStorage();
        setAuthToken();
        setJWTAuthData({
            user: null,
            isLoading: false,
            isAuthenticated: false,
            inProgress
        });
    };

    return (
        <JWTAuthContext.Provider value={{ ...userDetailsData, setJWTAuthData, isSSOlogin, refreshFolders }} >
            <JWTAuthActionsContext.Provider value={{ signInUser, ssoSignInUser, logout, resetUserPassword, changeUserPassword, retrySsoSignIn }}>
                {
                    children
                }
            </JWTAuthActionsContext.Provider>
        </JWTAuthContext.Provider>);
};
export default JWTAuthProvider;